import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { History, TrendingUp, TrendingDown, Minus, Loader2, HelpCircle } from "lucide-react";

const WINDOWS = [
  { d: 30, it: "30 giorni", en: "30 days" },
  { d: 90, it: "90 giorni", en: "90 days" },
  { d: 365, it: "365 giorni", en: "365 days" },
  { d: 0, it: "Tutto", en: "All" },
];
const TOLS = [3, 5, 10];

const esitoStyle = {
  above: { bg: "bg-emerald-500/10", c: "text-emerald-600 dark:text-emerald-400", it: "Sopra il consiglio", en: "Above" },
  inline: { bg: "bg-secondary", c: "text-muted-foreground", it: "In linea", en: "In line" },
  below: { bg: "bg-amber-500/10", c: "text-amber-600 dark:text-amber-400", it: "Sotto il consiglio", en: "Below" },
};

export default function DecisionHistory() {
  const { lang, properties, activeId } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const active = properties.find((p) => p.id === activeId) || properties[0];
  const [days, setDays] = useState(90);
  const [tol, setTol] = useState(5);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    api.get(`/properties/${active.id}/decision-history?days=${days}&tol=${tol}&lang=${lang}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [active, days, tol, lang]);

  const s = data?.summary;
  const money = (v) => (v == null ? "—" : `€${Number(v).toLocaleString(lang === "it" ? "it-IT" : "en-GB")}`);

  const Verdict = ({ v, diff }) => {
    if (v === "better") return <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold" title={L("Hai fatto meglio di Revenue AI", "You did better than Revenue AI")}>✅ +€{Math.abs(diff)}</span>;
    if (v === "worse") return <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 font-semibold" title={L("Hai guadagnato meno del previsto dal modello", "You earned less than the model expected")}>⚠️ €{diff}</span>;
    if (v === "equal") return <span className="inline-flex items-center gap-1 text-muted-foreground" title={L("Decisione equivalente", "Equivalent decision")}>🟢 {L("equivalente", "equal")}</span>;
    return <span className="inline-flex items-center gap-1 text-muted-foreground/60" title={L("Impossibile valutare (dati insufficienti)", "Cannot evaluate (insufficient data)")}>❔</span>;
  };

  return (
    <div className="App min-h-screen"><Nav />
      <div className="max-w-[1400px] mx-auto px-6 py-8" data-testid="decision-history-page">
        <div className="flex items-center gap-3 mb-6">
          <History className="w-7 h-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">{L("Storico decisioni AI", "AI Decision History")}</h1>
            <p className="text-sm text-muted-foreground">{L("Consigliato vs impostato, con l'esito reale delle tue vendite.", "Recommended vs set, with the real outcome of your sales.")}</p>
          </div>
        </div>

        {/* controls */}
        <div className="flex items-center gap-4 mb-5 flex-wrap">
          <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-1" data-testid="dh-window">
            {WINDOWS.map((w) => (
              <button key={w.d} onClick={() => setDays(w.d)} data-testid={`dh-window-${w.d}`}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${days === w.d ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>
                {L(w.it, w.en)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">{L("Soglia \"seguito\"", "Follow tolerance")}:</span>
            <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-1">
              {TOLS.map((t) => (
                <button key={t} onClick={() => setTol(t)} data-testid={`dh-tol-${t}`}
                  className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${tol === t ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`}>±{t}%</button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> {L("Caricamento…", "Loading…")}</div>
        ) : !data ? (
          <div className="text-muted-foreground py-12 text-center">{L("Nessun dato disponibile.", "No data available.")}</div>
        ) : (
          <>
            {/* headline */}
            {s?.headline && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 mb-5 text-sm" data-testid="dh-headline">{s.headline}</div>
            )}

            {/* summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="dh-summary">
              <Card label={L("Consigli seguiti", "Followed")} value={s.followed_rate != null ? `${s.followed_rate}%` : L("Dati insuff.", "Insuff.")} />
              <Card label={L("Ricavo totale", "Total revenue")} value={money(s.total_revenue)} />
              <Card label={L("Se seguito sempre l'AI", "If always followed AI")} value={s.ai_revenue_if_followed != null ? money(s.ai_revenue_if_followed) : L("Dati insuff.", "Insuff.")} />
              <Card label={L("Differenza", "Difference")} value={s.difference_eur != null ? `${s.difference_eur >= 0 ? "+" : ""}${money(s.difference_eur)} (${s.difference_pct}%)` : "—"}
                color={s.difference_eur == null ? "" : s.difference_eur >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"} />
              <Card label={L("Occ. media", "Avg occ.")} value={s.avg_occupancy != null ? `${s.avg_occupancy}%` : "—"} />
              <Card label={L("ADR medio", "Avg ADR")} value={money(s.avg_adr)} />
              <Card label={L("RevPAR medio", "Avg RevPAR")} value={money(s.avg_revpar)} />
            </div>

            {/* table */}
            <div className="bg-card border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[1100px]" data-testid="dh-table">
                <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left font-bold px-3 py-2.5">{L("Data", "Date")}</th>
                    <th className="text-right font-bold px-3 py-2.5">{L("Consigliato", "Recommended")}</th>
                    <th className="text-right font-bold px-3 py-2.5">{L("Impostato", "Set")}</th>
                    <th className="text-right font-bold px-3 py-2.5">{L("Scost. €", "Dev. €")}</th>
                    <th className="text-right font-bold px-3 py-2.5">{L("Scost. %", "Dev. %")}</th>
                    <th className="text-right font-bold px-3 py-2.5">{L("Occ.", "Occ.")}</th>
                    <th className="text-right font-bold px-3 py-2.5">{L("Vendute", "Sold")}</th>
                    <th className="text-right font-bold px-3 py-2.5">{L("Invendute", "Unsold")}</th>
                    <th className="text-right font-bold px-3 py-2.5">ADR</th>
                    <th className="text-right font-bold px-3 py-2.5">RevPAR</th>
                    <th className="text-right font-bold px-3 py-2.5">{L("Ricavo", "Revenue")}</th>
                    <th className="text-center font-bold px-3 py-2.5">{L("Esito", "Result")}</th>
                    <th className="text-center font-bold px-3 py-2.5">{L("Decisione migliore?", "Better call?")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 ? (
                    <tr><td colSpan={13} className="text-center text-muted-foreground py-8">{L("Nessuna riga nel periodo.", "No rows in range.")}</td></tr>
                  ) : data.items.map((r, i) => {
                    const es = r.esito ? esitoStyle[r.esito] : null;
                    return (
                      <tr key={r.date} data-testid={`dh-row-${i}`} className="border-t border-border hover:bg-accent/30">
                        <td className="px-3 py-2 font-data whitespace-nowrap">{r.date}{!r.past && <span className="ml-1 text-[10px] text-primary">{L("futuro", "future")}</span>}</td>
                        <td className="px-3 py-2 text-right font-data">{r.recommended_price != null ? `€${r.recommended_price}` : <span className="text-muted-foreground/50 text-xs">{L("Dati insuff.", "N/A")}</span>}</td>
                        <td className="px-3 py-2 text-right font-data">{r.set_price != null ? `€${r.set_price}` : <span className="text-muted-foreground/50 text-xs">{L("Dati insuff.", "N/A")}</span>}</td>
                        <td className="px-3 py-2 text-right font-data" style={{ color: r.deviation_eur == null ? "" : r.deviation_eur > 0 ? "var(--demand-low)" : r.deviation_eur < 0 ? "var(--demand-high)" : "" }}>{r.deviation_eur != null ? `${r.deviation_eur > 0 ? "+" : ""}${r.deviation_eur}` : "—"}</td>
                        <td className="px-3 py-2 text-right font-data text-muted-foreground">{r.deviation_pct != null ? `${r.deviation_pct > 0 ? "+" : ""}${r.deviation_pct}%` : "—"}</td>
                        <td className="px-3 py-2 text-right font-data">{r.occupancy != null ? `${r.occupancy}%` : "—"}</td>
                        <td className="px-3 py-2 text-right font-data">{r.rooms_sold != null ? r.rooms_sold : "—"}</td>
                        <td className="px-3 py-2 text-right font-data text-muted-foreground">{r.rooms_unsold != null ? r.rooms_unsold : "—"}</td>
                        <td className="px-3 py-2 text-right font-data">{r.adr != null ? `€${r.adr}` : "—"}</td>
                        <td className="px-3 py-2 text-right font-data text-primary">{r.revpar != null ? `€${r.revpar}` : "—"}</td>
                        <td className="px-3 py-2 text-right font-data font-semibold">{r.revenue != null ? `€${r.revenue}` : "—"}</td>
                        <td className="px-3 py-2 text-center">{es ? <span className={`text-xs px-2 py-0.5 rounded-full ${es.bg} ${es.c}`}>{L(es.it, es.en)}</span> : "—"}</td>
                        <td className="px-3 py-2 text-center whitespace-nowrap"><Verdict v={r.verdict} diff={r.verdict_diff_eur} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
              <HelpCircle className="w-3.5 h-3.5" />
              {L("Consigliato e Impostato si popolano man mano che Revenue AI registra le decisioni giornaliere. ADR, RevPAR, occupazione e ricavo vengono dai tuoi dati reali importati.",
                 "Recommended and Set fill in as Revenue AI records daily decisions. ADR, RevPAR, occupancy and revenue come from your imported real data.")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const Card = ({ label, value, color = "" }) => (
  <div className="bg-card border border-border rounded-lg p-3">
    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
    <div className={`text-lg font-bold ${color}`}>{value}</div>
  </div>
);
